import React from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Option } from '../../../types/Option';
import { PilotId } from '../../../types/PilotId';
import { SharedSelectProps } from '../../../types/SharedSelectProps';
import { User as UserType } from '../../../types/User';
import { getUserName } from '../../../utils/getUserName';
import { typedMemo } from '../../../utils/typedMemo';
import { User } from '../../User/User';
import { Select } from '../Select';

interface Props<M, Role> extends SharedSelectProps<PilotId | Role, M> {
  /** Users that will be available as options. */
  users: UserType[];
  /** Enable user emails */
  enableEmail?: boolean;
  /** Enable select all */
  enableSelectAll?: boolean;
  /** Roles that will be available as options. */
  roles?: Option<Role>[];
  /** Serialize contextual user info (activity, timestamp, role, email) */
  serializeUserInfo?: (user: UserType) => string;
}

export const UserSelect = typedMemo(
  <M extends boolean, Role>({
    enableEmail = true,
    roles,
    users,
    value,
    onChange,
    serializeUserInfo,
    ...rest
  }: Props<M, Role>) => {
    const intl = useIntl();
    const options = users.map(
      transformUserOption({ enableEmail, serializeUserInfo, intl }),
    );
    const enhancedOptions =
      roles && roles.length ? [...roles, ...options] : options;
    return (
      <Select
        placeholder={intl.formatMessage({
          id: 'UserSelect.placeholder',
          defaultMessage: 'Select user...',
          description: 'Placeholder text for user select component',
        })}
        {...rest}
        options={enhancedOptions}
        value={value}
        onChange={onChange}
      />
    );
  },
);

export const transformUserOption =
  ({
    serializeUserInfo,
    enableEmail,
    intl,
  }: {
    serializeUserInfo?: (user: UserType) => string;
    enableEmail?: boolean;
    intl: IntlShape;
  }) =>
  (user: UserType): Option<PilotId> => ({
    value: user.id,
    label: getUserName({
      user,
      options: { isDeleted: user.isDeleted },
      intl,
    }),
    leftContent: (
      <User mode="avatar" options={{ disableTooltip: true }} user={user} />
    ),
    description: serializeUserInfo
      ? serializeUserInfo(user)
      : (enableEmail && user.email) || undefined,
  });
